import { getTitle } from "./helper";

/**
 * Returns variant display strings like ["Size: Large", "Color: Red"] for a cart/order item.
 * Looks up the item by itemId in menusData to resolve variantId → human-readable names.
 */
export const getVariantDisplay = (item: any, menusData?: any): string[] => {
  if (!item) return [];

  // The persisted snapshot is authoritative — it survives variant/option
  // deletion and needs no menu lookup. Handles both plain strings (cart-side)
  // and MultiLanguageText objects (API order snapshot).
  if (item.variantName) return [getTitle(item.variantName)];

  if (!menusData || !item?.itemId) return [];

  const allItems =
    menusData?.menus?.flatMap(
      (m: any) => m.sections?.flatMap((s: any) => s.items || []) || [],
    ) || [];

  const menuItem = allItems.find((i: any) => i.itemId === item.itemId);
  if (!menuItem) return [];

  const variants = menuItem?.variants || [];
  const attributes = menuItem?.attributes || [];

  // Case 1: variantId present — find the specific variant
  if (item.variantId) {
    const variant = variants.find((v: any) => v.variantId === item.variantId);
    if (variant?.name) return [getTitle(variant.name)];
    if (variant?.attributeValues?.length > 0) {
      return variant.attributeValues
        .map((av: any) => {
          const attr = attributes.find((a: any) => a.attributeId === av.attributeId);
          const option = attr?.options?.find(
            (o: any) => o.attributeOptionId === av.attributeOptionId,
          );
          const attrName = attr ? getTitle(attr.name) : "";
          const optName = option ? getTitle(option.name) : "";
          return attrName ? `${attrName}: ${optName}` : optName;
        })
        .filter(Boolean);
    }
  }

  // Case 2: attributeValues directly (from order items)
  if (item.attributeValues?.length > 0) {
    return item.attributeValues
      .map((av: any) => {
        const attr = attributes.find((a: any) => a.attributeId === av.attributeId);
        const option = attr?.options?.find(
          (o: any) => o.attributeOptionId === av.attributeOptionId,
        );
        const attrName = attr ? getTitle(attr.name) : "";
        const optName = option ? getTitle(option.name) : "";
        return attrName ? `${attrName}: ${optName}` : optName;
      })
      .filter(Boolean);
  }

  return [];
};
